import './index.scss';

import { useState } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMenuProfile } from '@/_hooks/useMenuProfile';
import { Icon } from '@/components/Icon';
import { UserAvatar } from '@/components/UserAvatar';
import type { RootState } from '@/store/store';
import { SUPPORT_ITEM_KEY, UPDATES_ITEM_KEY, type NavItem } from '../navigation';

type ProfileMenuProps = {
  /** The account entry of `SideMenuItems`: profile links, support, updates and the logout action. */
  accountItem?: NavItem;
  collapsed?: boolean;
  onNavigate?: () => void;
  onOpenUpdates?: () => void;
  onOpenSupport?: () => void;
};

const isProfileValue = (value?: string) => Boolean(value?.trim()) && value?.trim() !== '-';

export const ProfileMenu = ({
  accountItem,
  collapsed = false,
  onNavigate,
  onOpenUpdates,
  onOpenSupport,
}: ProfileMenuProps) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { userProfile } = useMenuProfile();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const profileName =
    [userProfile?.firstName, userProfile?.lastName].filter(isProfileValue).join(' ') ||
    [authUser?.firstName, authUser?.lastName].filter(isProfileValue).join(' ') ||
    t('common:common.user_profile_settings');
  const profileRoles = userProfile?.roles?.length
    ? userProfile.roles.join(', ')
    : t('common:common.role_undefined');
  const links = accountItem?.children?.filter((item) => !item.onClick) ?? [];
  const logoutItem = accountItem?.children?.find((item) => item.onClick);

  const closeMenu = () => {
    setIsOpen(false);
    onNavigate?.();
  };

  const handleSelect = (item: NavItem) => {
    closeMenu();

    if (item.key === UPDATES_ITEM_KEY) {
      onOpenUpdates?.();
      return;
    }

    if (item.key === SUPPORT_ITEM_KEY) {
      onOpenSupport?.();
      return;
    }

    if (item.path) {
      navigate(item.path);
    }
  };

  // The trigger already shows the photo, name and role, so the menu starts with the links.
  const profileDropdown = (
    <div className="c-profile-menu" role="menu">
      {links.length > 0 && (
        <>
          <div className="c-profile-menu__actions">
            {links.map((item) =>
              item.href ? (
                <a
                  key={item.key}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  role="menuitem"
                  className="c-profile-menu__item"
                  onClick={closeMenu}
                >
                  {item.icon}
                  <span>{item.label}</span>
                  <ArrowRightOutlined className="c-profile-menu__item-arrow" />
                </a>
              ) : (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className="c-profile-menu__item"
                  onClick={() => handleSelect(item)}
                >
                  {item.icon}
                  <span>{item.label}</span>
                  <ArrowRightOutlined className="c-profile-menu__item-arrow" />
                </button>
              )
            )}
          </div>
        </>
      )}
      {logoutItem && (
        <>
          <div className="c-profile-menu__divider" />
          <button
            type="button"
            role="menuitem"
            className="c-profile-menu__item c-profile-menu__item-danger"
            onClick={() => {
              closeMenu();
              logoutItem.onClick?.();
            }}
          >
            {logoutItem.icon}
            <span>{logoutItem.label}</span>
          </button>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      open={isOpen}
      placement="topLeft"
      popupRender={() => profileDropdown}
      trigger={['click']}
      onOpenChange={setIsOpen}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={collapsed ? profileName : undefined}
        className={`c-profile-trigger c-profile-trigger--sidebar${
          collapsed ? ' c-profile-trigger--collapsed' : ''
        }`}
      >
        <UserAvatar
          size={24}
          src={userProfile?.profilePicture}
          alt={profileName}
          name={profileName}
        />
        {!collapsed && (
          <span className="c-profile-trigger__details">
            <strong className="c-profile-trigger__name">{profileName}</strong>
            <span className="c-profile-trigger__title">{profileRoles}</span>
          </span>
        )}
        {!collapsed && <Icon name="chevronUp" size={16} className="c-profile-trigger__chevron" />}
      </button>
    </Dropdown>
  );
};
